import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  useLoadScript,
} from "@react-google-maps/api";
import getAddressLatLong from "../utils/getAddressLatLong";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import styles from "../../../src/styles/location.module.css";

const AllLocationMapModal = ({
  locationMapModal,
  setLocationMapModal,
  locations,
}) => {
  const mapContainerStyle = {
    width: "100%",
    height: "95vh",
  };

  const defaultCenter = {
    lat: 20.5937,
    lng: 78.9629,
  };

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationsWithCoordinates, setLocationsWithCoordinates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const mapRef = useRef(null);

  // Fetch coordinates for all locations
  useEffect(() => {
    if (locations?.length > 0) {
      const fetchCoordinates = async () => {
        setIsLoading(true);
        const results = await Promise.all(
          locations.map(async (loc) => {
            const { address, city, state, zipCode } = loc;
            const coords = await getAddressLatLong(
              address,
              city,
              state,
              zipCode,
            );
            return { ...loc, locationCoordinates: coords };
          }),
        );
        setLocationsWithCoordinates(results);
        setIsLoading(false);
      };

      fetchCoordinates();
    }
  }, [locations]);

  const getPosition = (coords) => {
    if (!coords) return null;

    if (coords.lat && coords.lng)
      return { lat: Number(coords.lat), lng: Number(coords.lng) };

    if (coords.latitude && coords.longitude)
      return { lat: Number(coords.latitude), lng: Number(coords.longitude) };

    return null;
  };

  const filteredLocations = useMemo(() => {
    if (!locationsWithCoordinates) return [];

    const seen = new Set();
    const duplicatesMap = new Map();
    const unique = [];

    locationsWithCoordinates.forEach((loc) => {
      const { lat, lng } = loc.locationCoordinates || {};
      if (!lat || !lng) return;

      const key = `${lat},${lng}`;
      console.log("key", key);

      if (seen.has(key)) {
        // 👇 SAME lat-lng mil gaya
        if (!duplicatesMap.has(key)) {
          duplicatesMap.set(key, []);
        }
        duplicatesMap.get(key).push(loc);
      } else {
        seen.add(key);
        unique.push({
          details: loc,
          locationCoordinates: loc.locationCoordinates,
        });
      }
    });

    // 🔥 YAHAN CONSOLE KARWA RAHE HAIN
    duplicatesMap.forEach((locations, key) => {
      console.group(`Duplicate location found → ${key}`);
      locations.forEach((loc, index) => {
        console.log(`Duplicate #${index + 1}`, loc);
      });
      console.groupEnd();
    });

    return unique;
  }, [locationsWithCoordinates]);

  const [mapCenter, setMapCenter] = useState(defaultCenter);

  useEffect(() => {
    if (filteredLocations.length > 0) {
      const pos = getPosition(filteredLocations[0].locationCoordinates);
      if (pos) setMapCenter(pos);
    }
  }, [filteredLocations]);

  const fitBoundsToAllMarkers = (map) => {
    if (!map) return;

    const bounds = new window.google.maps.LatLngBounds();
    let added = 0;

    const tryAdd = (coords) => {
      const pos = getPosition(coords);
      if (pos) {
        bounds.extend(pos);
        added++;
      }
    };

    filteredLocations.forEach((loc) => tryAdd(loc.locationCoordinates));

    if (added === 0) {
      map.setCenter(defaultCenter);
      setMapCenter(defaultCenter);
      return;
    }

    map.fitBounds(bounds);

    const c = bounds.getCenter();
    setMapCenter({ lat: c.lat(), lng: c.lng() });
  };

  useEffect(() => {
    if (mapRef.current) fitBoundsToAllMarkers(mapRef.current);
  }, [filteredLocations]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries: ["places"],
  });

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div className="text-center">Loading...</div>;

  return (
    <Dialog
      open={locationMapModal}
      onClose={() => setLocationMapModal(false)}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <h3 className="font-[600]">Stores: {filteredLocations.length}</h3>
          <IconButton onClick={() => setLocationMapModal(false)}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: 600 }}>
        {isLoading ? (
          <div className="flex justify-center items-center  h-[40rem] w-full">
            <CircularProgress />
          </div>
        ) : filteredLocations.length > 0 ? (
          <>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={10}
              onLoad={(map) => {
                mapRef.current = map;
                fitBoundsToAllMarkers(map);
              }}
              options={{
                mapTypeControl: false,
                streetViewControl: false,
              }}
            >
              {filteredLocations.map((location, i) => {
                const position = getPosition(location.locationCoordinates);
                if (!position) return null;

                return (
                  <Marker
                    key={`location-${i}`}
                    position={position}
                    onClick={() => setSelectedLocation(location)}
                  />
                );
              })}

              {selectedLocation && (
                <InfoWindow
                  position={{
                    lat:
                      getPosition(selectedLocation.locationCoordinates).lat +
                      0.0000005,
                    lng: getPosition(selectedLocation.locationCoordinates).lng,
                  }}
                  onCloseClick={() => setSelectedLocation(null)}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      width: 200,
                      padding: 0,
                      borderRadius: 0,
                      overflow: "hidden",
                    }}
                  >
                    <div className={styles.locationHeader}>
                      <h4 className={styles.storeName}>
                        {selectedLocation.details?.storeName}
                      </h4>
                    </div>

                    <div className={styles.detailsContainer}>
                      <div className={styles.detailsLabel}>
                        <p>Owner:</p>
                        <p>Contact:</p>
                        <p>Address:</p>
                      </div>
                      <div className={styles.detailsValue}>
                        <p>
                          {selectedLocation.details?.OwnerName ||
                            selectedLocation.details?.storeOwnerName}
                        </p>
                        <p>
                          {selectedLocation.details?.phoneNumber ||
                            selectedLocation.details?.storeOwnerPhoneNumber}
                        </p>
                        <p className={styles.address}>
                          {selectedLocation.details?.address},{" "}
                          {selectedLocation.details?.city},{" "}
                          {selectedLocation.details?.state},{" "}
                          {selectedLocation.details?.zipCode}
                        </p>
                      </div>
                    </div>
                  </Paper>
                </InfoWindow>
              )}
            </GoogleMap>
          </>
        ) : (
          <div className="text-center text-gray-500 py-4">
            No locations found.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AllLocationMapModal;
